import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOrders,
  updateOrderStatus,
  deleteOrder,
} from "../redux/AdminSlice";

import { addDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

import AdminProductManagement from "./AdminProductManagement";
import AdminNotificationListener from "./AdminNotificationListener";

/* ─── Design Tokens (GlossyLuxHair system) ───────────────── */
const T = {
  rose:       "#c0506e",
  roseDark:   "#8f2d46",
  roseLight:  "#e8839a",
  roseSoft:   "#fdf0f3",
  roseMid:    "#f3c5cf",
  ivory:      "#faf8f5",
  ivoryDark:  "#f0ece6",
  warm100:    "#e8e3dc",
  warm200:    "#d4cfc8",
  warm400:    "#9e9189",
  warm700:    "#3d3530",
  ink:        "#1a1510",
  surface:    "#ffffff",
  surfaceAlt: "#faf8f5",
  border:     "#e8e3dc",
  ffDisplay:  "'Cormorant Garamond', Georgia, serif",
  ffBody:     "'Outfit', system-ui, sans-serif",
};

/* ─── Status badge config ─────────────────────────────────── */
const STATUS_CFG = {
  Pending:   { bg: "#fff8ed", color: "#b45309", dot: "#f59e0b" },
  Shipped:   { bg: "#eff6ff", color: "#1d4ed8", dot: "#3b82f6" },
  Delivered: { bg: "#f0fdf4", color: "#15803d", dot: "#22c55e" },
};

/* ─── Stat card definitions ───────────────────────────────── */
const STAT_CARDS = [
  {
    key:  "stock",
    label: "Total Inventory",
    sub:  "Units in stock",
    icon: "📦",
    grad: `linear-gradient(135deg, ${  "#8f2d46"} 0%, ${"#c0506e"} 100%)`,
  },
  {
    key:  "sales",
    label: "Today's Revenue",
    sub:  "Sales today",
    icon: "💰",
    grad: "linear-gradient(135deg, #b45309 0%, #f59e0b 100%)",
  },
  {
    key:  "orders",
    label: "Orders Today",
    sub:  "Received today",
    icon: "🛍️",
    grad: "linear-gradient(135deg, #1d4ed8 0%, #60a5fa 100%)",
  },
];

/* ══════════════ Sub-components ══════════════════════════════ */

const AccentBar = () => (
  <div style={{
    height: 3,
    background: `linear-gradient(90deg,transparent 0%,${T.roseMid} 15%,${T.rose} 50%,${T.roseMid} 85%,transparent 100%)`,
  }} />
);

const Card = ({ children, style }) => (
  <div style={{
    background: T.surface,
    borderRadius: 16,
    border: `1px solid ${T.border}`,
    boxShadow: "0 2px 8px rgba(26,21,16,.04),0 10px 36px rgba(192,80,110,.05)",
    overflow: "hidden",
    ...style,
  }}>
    <AccentBar />
    <div style={{ padding: "clamp(1.25rem,3vw,2rem)" }}>{children}</div>
  </div>
);

const SectionTitle = ({ children, sub }) => (
  <div style={{ marginBottom: "1.25rem" }}>
    <h2 style={{
      fontFamily: T.ffDisplay,
      fontSize: "clamp(1.2rem,2.5vw,1.5rem)",
      fontWeight: 700,
      color: T.roseDark,
      letterSpacing: "-0.01em",
      margin: 0,
      lineHeight: 1.2,
    }}>{children}</h2>
    {sub && <p style={{ margin: "4px 0 0", fontSize: 13, color: T.warm400, fontFamily: T.ffBody }}>{sub}</p>}
  </div>
);

const StatusSelect = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onFocus={(e)  => (e.target.style.borderColor = T.rose)}
    onBlur={(e)   => (e.target.style.borderColor = T.warm200)}
    style={{
      padding: "6px 28px 6px 10px",
      fontFamily: T.ffBody,
      fontSize: 13,
      color: T.warm700,
      background: `${T.surfaceAlt} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%239e9189'/%3E%3C/svg%3E") no-repeat right 10px center`,
      backgroundSize: "10px 6px",
      border: `1.5px solid ${T.warm200}`,
      borderRadius: 8,
      outline: "none",
      cursor: "pointer",
      appearance: "none",
      WebkitAppearance: "none",
      transition: "border-color 180ms",
    }}
  >
    <option value="Pending">Pending</option>
    <option value="Shipped">Shipped</option>
    <option value="Delivered">Delivered</option>
  </select>
);

const IconBtn = ({ onClick, variant = "ghost", children }) => {
  const [hov, setHov] = useState(false);
  const danger = variant === "danger";
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: "5px 12px",
        fontFamily: T.ffBody,
        fontSize: 12,
        fontWeight: 600,
        borderRadius: 8,
        border: `1.5px solid ${danger ? (hov ? T.rose : T.roseMid) : (hov ? T.warm200 : T.warm100)}`,
        background: danger ? (hov ? T.roseSoft : "transparent") : (hov ? T.warm100 : "transparent"),
        color: danger ? T.roseDark : T.warm700,
        cursor: "pointer",
        transition: "all 180ms",
        whiteSpace: "nowrap",
      }}
    >{children}</button>
  );
};

const Textarea = ({ value, onChange, placeholder }) => {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={3}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding: "0.75rem 1rem",
        fontFamily: T.ffBody,
        fontSize: 14,
        color: T.ink,
        background: focused ? "#fff" : T.ivory,
        border: `1.5px solid ${focused ? T.rose : T.warm200}`,
        borderRadius: 10,
        outline: "none",
        resize: "vertical",
        lineHeight: 1.6,
        transition: "border-color 180ms,background 180ms,box-shadow 180ms",
        boxShadow: focused ? "0 0 0 3px rgba(192,80,110,.11)" : "none",
      }}
    />
  );
};

/* ─── Order Detail Modal ──────────────────────────────────── */
const OrderModal = ({ order, onClose }) => {
  if (!order) return null;

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(26,21,16,.45)",
        backdropFilter: "blur(4px)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        animation: "fadeIn 200ms ease both",
      }}
    >
      <div style={{
        width: "100%",
        maxWidth: 520,
        background: T.surface,
        borderRadius: 20,
        overflow: "hidden",
        boxShadow: "0 24px 80px rgba(26,21,16,.22)",
        animation: "slideUp 260ms cubic-bezier(0.16,1,0.3,1) both",
      }}>
        <AccentBar />

        {/* Header */}
        <div style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "1.5rem 1.75rem 1rem",
          borderBottom: `1px solid ${T.warm100}`,
        }}>
          <div>
            <h3 style={{ fontFamily: T.ffDisplay, fontSize: "1.4rem", fontWeight: 700, color: T.roseDark, margin: 0 }}>
              Order Details
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: T.warm400, fontFamily: T.ffBody }}>
              #{order.id?.slice(0, 8).toUpperCase()}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: T.warm100, border: "none", borderRadius: 8,
              width: 32, height: 32,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", fontSize: 14, color: T.warm700, flexShrink: 0,
            }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: "1.5rem 1.75rem", overflowY: "auto", maxHeight: "60vh" }}>
          <p style={{ fontFamily: T.ffBody, fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: T.rose, margin: "0 0 .75rem" }}>
            Customer
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1.5rem" }}>
            {[["Name", order.name], ["Email", order.email], ["Phone", order.phone], ["Address", order.address]].map(([label, val]) => (
              <div key={label} style={{
                background: T.surfaceAlt,
                border: `1px solid ${T.warm100}`,
                borderRadius: 10,
                padding: "0.65rem 0.85rem",
                gridColumn: label === "Address" ? "1 / -1" : "auto",
              }}>
                <p style={{ margin: 0, fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: T.warm400, fontFamily: T.ffBody }}>{label}</p>
                <p style={{ margin: "3px 0 0", fontSize: 13, color: T.warm700, fontFamily: T.ffBody, wordBreak: "break-word" }}>{val || "—"}</p>
              </div>
            ))}
          </div>

          <p style={{ fontFamily: T.ffBody, fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: T.rose, margin: "0 0 .75rem" }}>
            Items
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", marginBottom: "1.25rem" }}>
            {order.cartItems?.map((item, i) => (
              <div key={i} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "0.6rem 0.85rem",
                background: T.surfaceAlt, border: `1px solid ${T.warm100}`, borderRadius: 10, gap: "1rem",
              }}>
                <span style={{ fontFamily: T.ffBody, fontSize: 13, color: T.warm700 }}>
                  {item.name}
                  <span style={{ marginLeft: 6, color: T.warm400, fontSize: 12 }}>×{item.quantity}</span>
                </span>
                <span style={{ fontFamily: T.ffDisplay, fontWeight: 700, fontSize: 14, color: T.roseDark, whiteSpace: "nowrap" }}>
                  GH₵{(item.price * item.quantity).toFixed(2)}
                </span>
              </div>
            ))}
          </div>

          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "0.9rem 1rem",
            background: T.roseSoft, border: `1px solid ${T.roseMid}`, borderRadius: 12,
          }}>
            <span style={{ fontFamily: T.ffBody, fontSize: 13, fontWeight: 600, color: T.roseDark, letterSpacing: "0.06em", textTransform: "uppercase" }}>Total</span>
            <span style={{ fontFamily: T.ffDisplay, fontWeight: 700, fontSize: "1.3rem", color: T.roseDark }}>
              GH₵{Number(order.totalAmount).toFixed(2)}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "1rem 1.75rem 1.5rem", display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            onMouseEnter={(e) => { e.currentTarget.style.background = T.roseDark; e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = T.rose;     e.currentTarget.style.transform = "translateY(0)"; }}
            style={{
              padding: "0.65rem 1.75rem",
              fontFamily: T.ffBody, fontSize: 14, fontWeight: 600,
              color: "#fff", background: T.rose,
              border: "none", borderRadius: 10, cursor: "pointer",
              boxShadow: "0 4px 16px rgba(192,80,110,.25)",
              transition: "background 200ms,transform 180ms",
            }}
          >Close</button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════ Main Component ══════════════════════════════ */
const AdminPage = () => {
  const dispatch = useDispatch();
  const orders   = useSelector((state) => state.admin.orders);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [newsMessage,   setNewsMessage]   = useState("");
  const [newsError,     setNewsError]     = useState("");
  const [newsSuccess,   setNewsSuccess]   = useState("");
  const [totalStock,    setTotalStock]    = useState(0);
  const [todaySales,    setTodaySales]    = useState(0);
  const [ordersToday,   setOrdersToday]   = useState(0);
  const [postingNews,   setPostingNews]   = useState(false);

  useEffect(() => { dispatch(fetchOrders()); }, [dispatch]);

  const calculateStats = useCallback(async () => {
    try {
      const snap = await getDocs(collection(db, "products"));
      let stock = 0;
      snap.forEach((doc) => { stock += doc.data().quantity || 0; });
      setTotalStock(stock);

      const today = new Date().toISOString().split("T")[0];
      let sales = 0, count = 0;
      orders.forEach((o) => {
        if (o.timestamp?.seconds) {
          const d = new Date(o.timestamp.seconds * 1000).toISOString().split("T")[0];
          if (d === today) { sales += o.totalAmount || 0; count++; }
        }
      });
      setTodaySales(sales);
      setOrdersToday(count);
    } catch (err) {
      console.error("Stats error:", err);
    }
  }, [orders]);

  useEffect(() => { calculateStats(); }, [calculateStats]);

  const handleStatusChange = (orderId, newStatus) =>
    dispatch(updateOrderStatus({ orderId, newStatus }));

  const handleDelete = (orderId) => dispatch(deleteOrder(orderId));

  const handlePostNews = async () => {
    if (!newsMessage.trim()) {
      setNewsError("Please enter a news message.");
      setNewsSuccess("");
      return;
    }
    try {
      setPostingNews(true);
      await addDoc(collection(db, "news"), { message: newsMessage, timestamp: new Date() });
      setNewsSuccess("News posted successfully!");
      setNewsError("");
      setNewsMessage("");
    } catch (err) {
      console.error("News error:", err);
      setNewsError("Failed to post news. Try again.");
      setNewsSuccess("");
    } finally {
      setPostingNews(false);
    }
  };

  const statValues = {
    stock:  totalStock,
    sales:  `GH₵${todaySales.toFixed(2)}`,
    orders: ordersToday,
  };

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600&family=Outfit:wght@400;500;600&display=swap');
        @keyframes fadeIn  { from{opacity:0}  to{opacity:1} }
        @keyframes slideUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin    { to{transform:rotate(360deg)} }
        .adm-row { transition: background 160ms; }
        .adm-row:hover { background: ${T.surfaceAlt} !important; }
        ::-webkit-scrollbar       { width:6px; height:6px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:${T.warm200}; border-radius:99px; }
      `}</style>

      <div style={{
        minHeight: "100vh",
        background: `
          radial-gradient(ellipse 65% 45% at 95% 5%,  rgba(192,80,110,.05) 0%, transparent 65%),
          radial-gradient(ellipse 45% 35% at 5%  95%, rgba(192,80,110,.04) 0%, transparent 55%),
          ${T.ivoryDark}
        `,
        fontFamily: T.ffBody,
        padding: "clamp(1.5rem,5vw,3.5rem) clamp(1rem,4vw,2.5rem)",
      }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>

          <AdminNotificationListener />

          {/* Page header */}
          <div style={{ marginBottom: "clamp(1.75rem,4vw,2.75rem)" }}>
            <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: T.rose, margin: "0 0 .4rem", fontFamily: T.ffBody }}>
              GlossyLuxHair
            </p>
            <h1 style={{ fontFamily: T.ffDisplay, fontSize: "clamp(2rem,5vw,2.75rem)", fontWeight: 700, color: T.ink, letterSpacing: "-0.02em", lineHeight: 1.1, margin: 0 }}>
              Admin Dashboard
            </h1>
          </div>

          {/* Stat cards */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,240px),1fr))",
            gap: "clamp(1rem,2.5vw,1.5rem)",
            marginBottom: "clamp(1.75rem,4vw,2.5rem)",
          }}>
            {STAT_CARDS.map((c) => (
              <div key={c.key} style={{
                background: c.grad,
                borderRadius: 16,
                padding: "clamp(1.25rem,3vw,1.75rem)",
                color: "#fff",
                boxShadow: "0 8px 32px rgba(0,0,0,.12)",
                position: "relative",
                overflow: "hidden",
              }}>
                <div style={{ position: "absolute", right: -20, top: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,.08)" }} />
                <div style={{ position: "absolute", right: 20, bottom: -30, width: 70,  height: 70,  borderRadius: "50%", background: "rgba(255,255,255,.06)" }} />
                <p style={{ margin: "0 0 .25rem", fontSize: 12, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", opacity: .85, fontFamily: T.ffBody }}>{c.label}</p>
                <p style={{ margin: "0 0 .5rem", fontFamily: T.ffDisplay, fontSize: "clamp(1.75rem,4vw,2.25rem)", fontWeight: 700, lineHeight: 1 }}>{statValues[c.key]}</p>
                <p style={{ margin: 0, fontSize: 12, opacity: .75, fontFamily: T.ffBody }}>{c.sub}</p>
                <span style={{ position: "absolute", top: "1.25rem", right: "1.25rem", fontSize: "1.5rem" }}>{c.icon}</span>
              </div>
            ))}
          </div>

          {/* Orders table */}
          <Card style={{ marginBottom: "clamp(1.75rem,4vw,2.5rem)" }}>
            <SectionTitle sub={`${orders.length} total order${orders.length !== 1 ? "s" : ""}`}>Orders</SectionTitle>
            <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${T.warm100}` }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.ffBody, fontSize: 13 }}>
                <thead>
                  <tr style={{ background: T.surfaceAlt }}>
                    {["Customer", "Email", "Address", "Total", "Status", "Actions"].map((h) => (
                      <th key={h} style={{
                        padding: "0.75rem 1rem",
                        textAlign: "left",
                        fontWeight: 600,
                        fontSize: 11,
                        letterSpacing: "0.09em",
                        textTransform: "uppercase",
                        color: T.warm400,
                        borderBottom: `1px solid ${T.warm100}`,
                        whiteSpace: "nowrap",
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={{ padding: "2.5rem", textAlign: "center", color: T.warm400, fontFamily: T.ffDisplay, fontSize: "1.1rem" }}>
                        No orders yet
                      </td>
                    </tr>
                  ) : orders.map((order, i) => (
                    <tr
                      key={order.id}
                      className="adm-row"
                      style={{
                        background: order.status === "Pending" ? "#fffbf7" : "#fff",
                        borderBottom: i < orders.length - 1 ? `1px solid ${T.warm100}` : "none",
                      }}
                    >
                      <td style={{ padding: "0.85rem 1rem", color: T.warm700, fontWeight: 500 }}>{order.name}</td>
                      <td style={{ padding: "0.85rem 1rem", color: T.warm400, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{order.email}</td>
                      <td style={{ padding: "0.85rem 1rem", color: T.warm400, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{order.address || "—"}</td>
                      <td style={{ padding: "0.85rem 1rem", fontFamily: T.ffDisplay, fontWeight: 700, color: T.roseDark, whiteSpace: "nowrap" }}>
                        GH₵{Number(order.totalAmount).toFixed(2)}
                      </td>
                      <td style={{ padding: "0.85rem 1rem" }}>
                        <StatusSelect value={order.status} onChange={(val) => handleStatusChange(order.id, val)} />
                      </td>
                      <td style={{ padding: "0.85rem 1rem" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <IconBtn onClick={() => setSelectedOrder(order)}>View</IconBtn>
                          <IconBtn variant="danger" onClick={() => handleDelete(order.id)}>Delete</IconBtn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Post news */}
          <Card style={{ marginBottom: "clamp(1.75rem,4vw,2.5rem)" }}>
            <SectionTitle sub="Broadcast a message to your customers">Post Announcement</SectionTitle>
            <Textarea value={newsMessage} onChange={(e) => setNewsMessage(e.target.value)} placeholder="Write your announcement here…" />

            {newsError   && <p style={{ margin: "8px 0 0", fontSize: 13, color: T.rose,    fontFamily: T.ffBody }}>⚠️ {newsError}</p>}
            {newsSuccess && <p style={{ margin: "8px 0 0", fontSize: 13, color: "#15803d", fontFamily: T.ffBody }}>✓ {newsSuccess}</p>}

            <button
              onClick={handlePostNews}
              disabled={postingNews || !newsMessage.trim()}
              onMouseEnter={(e) => { if (newsMessage.trim()) { e.currentTarget.style.background = T.roseDark; e.currentTarget.style.transform = "translateY(-1px)"; } }}
              onMouseLeave={(e) => { e.currentTarget.style.background = newsMessage.trim() ? T.rose : T.warm200; e.currentTarget.style.transform = "translateY(0)"; }}
              style={{
                marginTop: "1rem",
                padding: "0.7rem 1.75rem",
                fontFamily: T.ffBody, fontSize: 14, fontWeight: 600, letterSpacing: "0.04em",
                color: "#fff",
                background: newsMessage.trim() ? T.rose : T.warm200,
                border: "none", borderRadius: 10,
                cursor: newsMessage.trim() && !postingNews ? "pointer" : "not-allowed",
                transition: "background 200ms,transform 180ms,box-shadow 200ms",
                boxShadow: newsMessage.trim() ? "0 4px 16px rgba(192,80,110,.22)" : "none",
                display: "inline-flex", alignItems: "center", gap: 8,
              }}
            >
              {postingNews
                ? <><span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin .7s linear infinite" }} /> Posting…</>
                : <><span>📢</span> Post Announcement</>
              }
            </button>
          </Card>

          <AdminProductManagement />
        </div>
      </div>

      {selectedOrder && <OrderModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />}
    </>
  );
};

export default AdminPage;